import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Select, Space, Table, TableColumnsType, Tag, Image, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import type { FormInstance } from 'antd/es/form';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { coffeeService } from '../services/coffeeService';
import { collectionService } from '../services/collectionService';
import type { CoffeeBean } from '../types/coffee';
import type { CoffeeCollection } from '../types/collection';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { bottledDrinkService } from '../services/bottledDrinkService';
import { BottledDrink } from '../types/bottledDrink';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';

const { Search } = Input;

const CollectionForm: React.FC<{
    onImageChange: (file: File | null) => void,
    selectedFileName: string | null,
    isMobile?: boolean,
    form: FormInstance,
    imagePreview?: string | null,
    imageFile: File | null,
    coffees: CoffeeBean[],
    drinks: BottledDrink[],
    catalogLoading: boolean,
    headlineFieldRef?: React.RefObject<any>,
    nameFieldRef?: React.RefObject<any>,
    descriptionFieldRef?: React.RefObject<any>,
    itemsFieldRef?: React.RefObject<any>,
    imageFieldRef?: React.RefObject<any>
}> = ({
    onImageChange,
    selectedFileName,
    isMobile = false,
    form,
    imagePreview,
    imageFile,
    coffees,
    drinks,
    catalogLoading,
    headlineFieldRef,
    nameFieldRef,
    descriptionFieldRef,
    itemsFieldRef,
    imageFieldRef
}) => {
    const previewUrl = useMemo(() => {
        if (!imageFile) {
            return null;
        }
        return URL.createObjectURL(imageFile);
    }, [imageFile]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const coffeeMap = useMemo(() => {
        return coffees.reduce<Record<string, CoffeeBean>>((accumulator, coffee) => {
            if (!coffee.id) {
                return accumulator;
            }
            accumulator[coffee.id] = coffee;
            return accumulator;
        }, {});
    }, [coffees]);

    const drinkMap = useMemo(() => {
        return drinks.reduce<Record<string, BottledDrink>>((accumulator, drink) => {
            if (!drink.id) {
                return accumulator;
            }
            accumulator[drink.id] = drink;
            return accumulator;
        }, {});
    }, [drinks]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        onImageChange(file || null);
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = useCallback((event: DragEndEvent, fieldListName: number[]) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = fieldListName.findIndex(
                name => `item-${name}` === active.id
            );
            const newIndex = fieldListName.findIndex(
                name => `item-${name}` === over.id
            );

            const currentItems = form.getFieldValue('items') || [];
            
            const newItems = arrayMove(currentItems, oldIndex, newIndex);
            
            form.setFieldsValue({
                items: newItems
            });
        }
    }, [form]);

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'align',
        'link'
    ];

    return (
        <div className={`admin-form-container ${isMobile ? 'mobile-coffee-form' : ''}`}>
            <div className="admin-form-section">
                <h3 className="admin-form-section-title">Thông tin cơ bản</h3>
                <div ref={headlineFieldRef}>
                    <Form.Item name="headline" label="Tiêu đề tuyển tập" rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tuyển tập' }]}>
                        <Input placeholder="VD: Người nổi tiếng uống gì" />
                    </Form.Item>
                </div>

                <div ref={nameFieldRef}>
                    <Form.Item name="name" label="Tên tuyển tập" rules={[{ required: true, message: 'Vui lòng nhập tên tuyển tập' }]}>
                        <Input placeholder="VD: Top 5 loại cà phê tốt nhất 2024" />
                    </Form.Item>
                </div>

                <div ref={descriptionFieldRef}>
                    <Form.Item
                        name="description"
                        label="Mô tả"
                        rules={[{ required: true, message: 'Vui lòng nhập mô tả tuyển tập' }]}
                        style={{ height: 'auto' }}
                    >
                        <ReactQuill
                            theme="snow"
                            modules={modules}
                            formats={formats}
                            style={{ height: '200px', marginBottom: '50px' }}
                        />
                    </Form.Item>
                </div>
            </div>

            <div className="admin-form-section" ref={itemsFieldRef}>
                <h3 className="admin-form-section-title">Danh sách sản phẩm</h3>
                <Form.List
                    name="items"
                    rules={[
                        {
                            validator: async (_, value) => {
                                if (!value || value.length < 1) {
                                    return Promise.reject('Vui lòng chọn ít nhất một sản phẩm cho tuyển tập');
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(event) => handleDragEnd(event, fields.map(f => f.name))}
                            >
                                <SortableContext
                                    items={fields.map(field => `item-${field.name}`)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {fields.map((field) => {
                                        const fieldValue = form.getFieldValue(['items', field.name]);
                                        const itemType = fieldValue?.type || 'coffee';
                                        const item = itemType === 'coffee'
                                            ? coffeeMap[fieldValue?.itemId]
                                            : drinkMap[fieldValue?.itemId];
                                        
                                        return (
                                            <div key={field.key}>
                                                {item ? (
                                                    <SortableItem
                                                        id={`item-${field.name}`}
                                                        item={item}
                                                        onRemove={() => remove(field.name)}
                                                        type={itemType}
                                                    />
                                                ) : (
                                                    <Space.Compact style={{ width: '100%' }}>
                                                        <Form.Item
                                                            name={[field.name, 'type']}
                                                            initialValue="coffee"
                                                            style={{ width: '30%', marginBottom: 8 }}
                                                        >
                                                            <Select
                                                                onChange={() => {
                                                                    // Reset itemId when type changes
                                                                    const newValues = [...form.getFieldValue('items')];
                                                                    newValues[field.name] = { 
                                                                        ...newValues[field.name],
                                                                        itemId: undefined 
                                                                    };
                                                                    form.setFieldsValue({ items: newValues });
                                                                }}
                                                            >
                                                                <Select.Option value="coffee">Cà phê</Select.Option>
                                                                <Select.Option value="drink">Đồ uống đóng chai</Select.Option>
                                                            </Select>
                                                        </Form.Item>

                                                        <Form.Item
                                                            name={[field.name, 'itemId']}
                                                            rules={[{ required: true, message: 'Vui lòng chọn sản phẩm' }]}
                                                            style={{ width: '70%', marginBottom: 8 }}
                                                        >
                                                            <Select
                                                                loading={catalogLoading}
                                                                placeholder={`Chọn ${fieldValue?.type === 'coffee' ? 'cà phê' : 'đồ uống'}`}
                                                                onChange={(value) => {
                                                                    const newValues = [...form.getFieldValue('items')];
                                                                    newValues[field.name] = { 
                                                                        ...newValues[field.name],
                                                                        itemId: value,
                                                                        order: field.name 
                                                                    };
                                                                    form.setFieldsValue({ items: newValues });
                                                                }}
                                                            >
                                                                {(fieldValue?.type === 'coffee' ? coffees : drinks).map(item => (
                                                                    <Select.Option key={item.id} value={item.id}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            {item.driveImages && item.driveImages[0] ? (
                                                                                <img
                                                                                    src={`https://lh3.googleusercontent.com/d/${item.driveImages[0].fileId}?authuser=server`}
                                                                                    alt={item.name}
                                                                                    style={{ width: 20, height: 20, objectFit: 'cover' }}
                                                                                />
                                                                            ) : item.images && item.images[0] ? (
                                                                                <img
                                                                                    src={item.images[0]}
                                                                                    alt={item.name}
                                                                                    style={{ width: 20, height: 20, objectFit: 'cover' }}
                                                                                />
                                                                            ) : null}
                                                                            {item.name}
                                                                        </div>
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>

                                                        <Button 
                                                            type="link" 
                                                            danger 
                                                            onClick={() => remove(field.name)}
                                                            style={{ marginBottom: 8 }}
                                                        >
                                                            Xóa
                                                        </Button>
                                                    </Space.Compact>
                                                )}
                                            </div>
                                        );
                                    })}
                                </SortableContext>
                            </DndContext>

                            <Button type="dashed" onClick={() => add()} block>
                                + Thêm sản phẩm vào tuyển tập
                            </Button>

                            <Form.ErrorList errors={errors} className="ant-form-item-explain-error" />
                        </>
                    )}
                </Form.List>
            </div>

            <div className="admin-form-section" ref={imageFieldRef}>
                <h3 className="admin-form-section-title">Hình ảnh</h3>
                <Form.Item name="image" label="Hình ảnh tuyển tập" rules={[{ required: true, message: 'Vui lòng chọn hình ảnh tuyển tập' }]}>
                    <div className="admin-image-upload-section">
                        <Space direction="vertical">
                            <Button onClick={() => document.getElementById('fileInput')?.click()}>
                                Chọn ảnh
                            </Button>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                id="fileInput"
                                style={{ display: 'none' }}
                            />
                            {(selectedFileName || imagePreview) && (
                                <div style={{ marginTop: 8 }}>
                                    {selectedFileName && (
                                        <div>
                                            <Tag color="blue">
                                                File đã chọn: {selectedFileName}
                                            </Tag>
                                            <div style={{ marginTop: 8 }}>
                                                <Image
                                                    src={previewUrl || ''}
                                                    alt="New collection preview"
                                                    style={{ width: 200, objectFit: 'cover' }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {imagePreview && !selectedFileName && (
                                        <div>
                                            <div>Ảnh hiện tại:</div>
                                            <Image
                                                src={imagePreview}
                                                alt="Collection preview"
                                                style={{ width: 200, objectFit: 'cover' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </Space>
                    </div>
                </Form.Item>
            </div>
        </div>
    );
};

const SortableItem = ({
    id,
    item,
    onRemove,
    type
}: {
    id: string;
    item: any;
    onRemove: () => void;
    type: 'coffee' | 'drink';
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        cursor: 'move',
        padding: '8px',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        marginBottom: '8px',
        backgroundColor: 'white',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {item.driveImages && item.driveImages[0] ? (
                        <img
                            src={`https://lh3.googleusercontent.com/d/${item.driveImages[0].fileId}?authuser=server`}
                            alt={item.name}
                            style={{ width: 40, height: 40, objectFit: 'cover' }}
                        />
                    ) : item.images && item.images[0] ? (
                        <img
                            src={item.images[0]}
                            alt={item.name}
                            style={{ width: 40, height: 40, objectFit: 'cover' }}
                        />
                    ) : null}
                    <div>
                        <div>{item.name}</div>
                        <Tag color={type === 'coffee' ? 'blue' : 'green'}>
                            {type === 'coffee' ? 'Cà phê' : 'Đồ uống đóng chai'}
                        </Tag>
                    </div>
                </div>
                <Button onClick={onRemove} type="link" danger>
                    Xóa
                </Button>
            </div>
        </div>
    );
};

const CollectionManagement: React.FC = () => {
    const [collections, setCollections] = useState<CoffeeCollection[]>([]);
    const [loading, setLoading] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCollection, setEditingCollection] = useState<CoffeeCollection | null>(null);
    const [form] = Form.useForm();
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 400;
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [coffees, setCoffees] = useState<CoffeeBean[]>([]);
    const [drinks, setDrinks] = useState<BottledDrink[]>([]);
    const collectionsRequestIdRef = useRef(0);
    const catalogLoadedRef = useRef(false);
    const catalogFetchPromiseRef = useRef<Promise<void> | null>(null);

    // Tour state and refs
    const [isTourOpen, setIsTourOpen] = useState(false);
    const [isModalTourOpen, setIsModalTourOpen] = useState(false);
    const titleRef = useRef(null);
    const searchRef = useRef(null);
    const addButtonRef = useRef(null);
    const tableContainerRef = useRef(null);

    // References for modal tour targets
    const modalTitleRef = useRef(null);
    const headlineFieldRef = useRef(null);
    const nameFieldRef = useRef(null);
    const descriptionFieldRef = useRef(null);
    const itemsFieldRef = useRef(null);
    const imageFieldRef = useRef(null);
    const submitButtonRef = useRef(null);

    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý tuyển tập',
            description: 'Chào mừng bạn đến với trang Quản lý tuyển tập! Đây là nơi bạn có thể tạo và quản lý các tuyển tập cà phê, đồ uống để giới thiệu đến khách hàng theo chủ đề hoặc theo sở thích cụ thể.',
            target: () => titleRef.current,
        },
        {
            title: 'Tìm kiếm',
            description: 'Tìm kiếm nhanh các tuyển tập theo tên hoặc tiêu đề. Nhập từ khóa và kết quả sẽ được lọc ngay lập tức.',
            target: () => searchRef.current,
        },
        {
            title: 'Thêm tuyển tập mới',
            description: 'Nhấn vào đây để tạo một tuyển tập mới. Bạn có thể tạo tuyển tập bao gồm nhiều loại cà phê và đồ uống, sắp xếp theo thứ tự mong muốn với tiêu đề và mô tả hấp dẫn.',
            target: () => addButtonRef.current,
        },
        {
            title: 'Bảng dữ liệu tuyển tập',
            description: 'Bảng hiển thị tất cả các tuyển tập đã được tạo. Mỗi tuyển tập bao gồm hình ảnh, tiêu đề, tên, số lượng sản phẩm và các nút thao tác để chỉnh sửa hoặc xóa.',
            target: () => tableContainerRef.current,
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng tạo và quản lý các tuyển tập cà phê, đồ uống để giới thiệu đến khách hàng một cách hấp dẫn và có tổ chức.',
            target: () => titleRef.current,
        },
    ];

    const modalTourSteps: TourProps['steps'] = [
        {
            title: 'Form thêm tuyển tập',
            description: 'Đây là form để thêm tuyển tập cà phê mới. Hãy điền đầy đủ các thông tin bắt buộc để tạo tuyển tập hoàn chỉnh với các sản phẩm được sắp xếp theo thứ tự mong muốn.',
            target: () => modalTitleRef.current,
        },
        {
            title: 'Tiêu đề tuyển tập',
            description: 'Nhập tiêu đề hấp dẫn cho tuyển tập (VD: "Người nổi tiếng uống gì", "Cà phê cho người mới bắt đầu"). Tiêu đề này sẽ thu hút sự chú ý của khách hàng. Đây là thông tin bắt buộc.',
            target: () => headlineFieldRef.current,
        },
        {
            title: 'Tên tuyển tập',
            description: 'Nhập tên chi tiết cho tuyển tập (VD: "Top 5 loại cà phê tốt nhất 2024", "Bộ sưu tập cà phê đặc biệt"). Tên này mô tả cụ thể nội dung của tuyển tập. Đây là thông tin bắt buộc.',
            target: () => nameFieldRef.current,
        },
        {
            title: 'Mô tả tuyển tập',
            description: 'Viết mô tả chi tiết về tuyển tập, lý do tạo ra tuyển tập này, đặc điểm của các sản phẩm trong tuyển tập. Sử dụng rich text editor để định dạng nội dung đẹp mắt. Đây là thông tin bắt buộc.',
            target: () => descriptionFieldRef.current,
        },
        {
            title: 'Danh sách sản phẩm',
            description: 'Thêm các sản phẩm vào tuyển tập từ danh sách cà phê và đồ uống có sẵn. Có thể chọn cả cà phê và đồ uống đóng chai. Kéo thả để sắp xếp thứ tự hiển thị. Cần có ít nhất một sản phẩm. Đây là thông tin bắt buộc.',
            target: () => itemsFieldRef.current,
        },
        {
            title: 'Hình ảnh tuyển tập',
            description: 'Chọn hình ảnh đại diện cho tuyển tập. Hình ảnh này sẽ hiển thị khi giới thiệu tuyển tập cho khách hàng. Nên chọn hình ảnh chất lượng cao, hấp dẫn. Đây là thông tin bắt buộc.',
            target: () => imageFieldRef.current,
        },
        {
            title: 'Hoàn thành',
            description: 'Kiểm tra lại tất cả thông tin và nhấn "Thêm mới" để lưu tuyển tập vào hệ thống. Đảm bảo đã điền đầy đủ các thông tin bắt buộc và các sản phẩm đã được sắp xếp theo thứ tự mong muốn.',
            target: () => submitButtonRef.current,
        },
    ];

    const fetchCollections = useCallback(async () => {
        const requestId = ++collectionsRequestIdRef.current;
        setLoading(true);

        try {
            const data = await collectionService.getAllCollections();
            if (requestId === collectionsRequestIdRef.current) {
                setCollections(data);
            }
        } catch (error) {
            if (requestId === collectionsRequestIdRef.current) {
                console.error('Error fetching collections:', error);
                message.error('Không thể tải danh sách tuyển tập');
            }
        } finally {
            if (requestId === collectionsRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, []);

    const ensureCatalogLoaded = useCallback(async () => {
        if (catalogLoadedRef.current) {
            return;
        }

        if (catalogFetchPromiseRef.current) {
            await catalogFetchPromiseRef.current;
            return;
        }

        setCatalogLoading(true);

        const fetchPromise = (async () => {
            try {
                const [coffeeData, drinkData] = await Promise.all([
                    coffeeService.getAllCoffees(),
                    bottledDrinkService.getAllBottledDrinks(),
                ]);
                setCoffees(coffeeData);
                setDrinks(drinkData);
                catalogLoadedRef.current = true;
            } catch (error) {
                console.error('Error fetching catalog data:', error);
                message.error('Không thể tải danh sách sản phẩm');
            } finally {
                setCatalogLoading(false);
            }
        })();

        catalogFetchPromiseRef.current = fetchPromise;
        await fetchPromise;
        catalogFetchPromiseRef.current = null;
    }, []);

    useEffect(() => {
        fetchCollections();
    }, [fetchCollections]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSubmit = useCallback(async (values: any) => {
        try {
            setSubmitLoading(true);

            const collectionData = {
                ...values,
                createdAt: new Date(),
                updatedAt: new Date()
            };

            if (editingCollection) {
                await collectionService.updateCollection(
                    editingCollection.id!,
                    collectionData,
                    imageFile || undefined
                );
                message.success('Cập nhật tuyển tập thành công');
            } else {
                await collectionService.addCollection(collectionData, imageFile || undefined);
                message.success('Thêm tuyển tập thành công');
            }

            setModalVisible(false);
            form.resetFields();
            setImageFile(null);
            setImagePreview(null);
            setEditingCollection(null);
            await fetchCollections();

            // Gọi api gửi dữ liệu tuyển tập để AI tự động cập nhật
            // await axios.get(`https://coffee-ai.8am.vn/update`);
        } catch (error) {
            console.error('Error saving collection:', error);
            message.error('Có lỗi xảy ra khi lưu thông tin');
        } finally {
            setSubmitLoading(false);
        }
    }, [editingCollection, fetchCollections, form, imageFile]);

    const handleDelete = useCallback((id: string) => {
        Modal.confirm({
            title: 'Xác nhận xóa',
            content: 'Bạn có chắc chắn muốn xóa tuyển tập này?',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    await collectionService.deleteCollection(id);
                    await fetchCollections();
                    message.success('Xóa tuyển tập thành công');

                    // Gọi api gửi dữ liệu tuyển tập để AI tự động cập nhật
                    // await axios.get(`https://coffee-ai.8am.vn/update`);   
                } catch (error) {
                    console.error('Error deleting collection:', error);
                    message.error('Không thể xóa tuyển tập');
                }
            }
        });
    }, [fetchCollections]);

    const openCreateModal = useCallback(() => {
        setEditingCollection(null);
        form.resetFields();
        setImageFile(null);
        setImagePreview(null);
        setModalVisible(true);
        void ensureCatalogLoaded();
    }, [ensureCatalogLoaded, form]);

    const openEditModal = useCallback((record: CoffeeCollection) => {
        setEditingCollection(record);
        setImageFile(null);
        setImagePreview(record.imageUrl || null);
        form.setFieldsValue(record);
        setModalVisible(true);
        void ensureCatalogLoaded();
    }, [ensureCatalogLoaded, form]);

    const columns: TableColumnsType<CoffeeCollection> = useMemo(() => [
        {
            title: 'Hình ảnh',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            render: (url: string) => url ? <img src={url} alt="collection" style={{ width: 100 }} /> : null
        },
        {
            title: 'Tiêu đề',
            dataIndex: 'headline',
            key: 'headline',
        },
        {
            title: 'Tên tuyển tập',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Số lượng sản phẩm',
            key: 'itemCount',
            render: (record: CoffeeCollection) => {
                if (!record.items) return '0';
                const coffeeCount = record.items.filter(item => item.type === 'coffee').length;
                const drinkCount = record.items.filter(item => item.type === 'drink').length;
                return (
                    <Space>
                        {coffeeCount > 0 && <Tag color="blue">{coffeeCount} cà phê</Tag>}
                        {drinkCount > 0 && <Tag color="green">{drinkCount} đồ uống</Tag>}
                    </Space>
                );
            }
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_: any, record: CoffeeCollection) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(record)}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record.id!)}
                    />
                </Space>
            )
        }
    ], [handleDelete, openEditModal]);

    const mobileColumns: TableColumnsType<CoffeeCollection> = useMemo(() => [
        {
            title: 'Thông tin',
            dataIndex: 'name',
            key: 'info',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        {record.imageUrl && (
                            <img
                                src={record.imageUrl}
                                alt="collection"
                                style={{ width: 40, height: 40, objectFit: 'cover' }}
                            />
                        )}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                {record.headline}
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                {record.name}
                            </div>
                        </div>
                    </div>

                    <div style={{ fontSize: '12px' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <div>
                                <strong>Mô tả:</strong>
                                <div style={{
                                    marginTop: 4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    color: '#666'
                                }}>
                                    {record.description}
                                </div>
                            </div>
                            <div>
                                <strong>Số lượng cà phê:</strong>{' '}
                                <Tag color="blue">{record.items?.filter(item => item.type === 'coffee').length || 0}</Tag>
                            </div>
                        </Space>
                    </div>
                </div>
            )
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(record)}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record.id!)}
                    />
                </Space>
            )
        }
    ], [handleDelete, openEditModal]);

    const filteredCollections = useMemo(() => {
        const normalizedSearchText = searchText.trim().toLowerCase();

        if (!normalizedSearchText) {
            return collections;
        }

        return collections.filter(collection => {
            const matchesSearch = collection.name.toLowerCase().includes(normalizedSearchText) ||
                collection.headline.toLowerCase().includes(normalizedSearchText) ||
                (collection.description?.toLowerCase() || '').includes(normalizedSearchText);
            return matchesSearch;
        });
    }, [collections, searchText]);

    const handleCloseModal = useCallback(() => {
        setModalVisible(false);
        setIsModalTourOpen(false); // Reset modal tour state
        form.resetFields();
        setImageFile(null);
        setImagePreview(null);
        setEditingCollection(null);
    }, [form]);

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý tuyển tập</h1>
                    <Tag color="blue">{collections.length} tuyển tập</Tag>
                    <Button 
                        type="primary" 
                        icon={<QuestionCircleOutlined />} 
                        onClick={() => setIsTourOpen(true)}
                        style={{ marginLeft: 8 }}
                    >
                        Hướng dẫn
                    </Button>
                </div>
                
                <div className="admin-page-actions">
                    <div className="admin-search-filters">
                        <div ref={searchRef}>
                            <Search
                                placeholder="Tìm theo tên hoặc tiêu đề"
                                allowClear
                                style={{ width: isMobile ? '100%' : 300 }}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                    </div>

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        className="admin-button"
                        ref={addButtonRef}
                    >
                        Thêm tuyển tập mới
                    </Button>
                </div>
            </div>

            <div className="admin-table-container" ref={tableContainerRef}>
                <Table
                    className="admin-table"
                    columns={isMobile ? mobileColumns : columns}
                    dataSource={filteredCollections}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: isMobile ? undefined : 1500 }}
                    pagination={{
                        responsive: true,
                        defaultPageSize: isMobile ? 5 : 10,
                        position: isMobile ? ['bottomCenter'] : ['bottomRight']
                    }}
                />
            </div>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>

            <Modal
                title={editingCollection ? "Sửa thông tin tuyển tập" : "Thêm tuyển tập mới"}
                visible={modalVisible}
                onCancel={handleCloseModal}
                footer={null}
                width={800}
                className="admin-modal"
                destroyOnClose={true}
            >
                <div className="admin-form-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 ref={modalTitleRef} style={{ margin: 0 }}>
                            {editingCollection ? "Cập nhật thông tin tuyển tập" : "Thêm tuyển tập mới"}
                        </h3>
                        <Button
                            type="primary"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => {
                                setIsModalTourOpen(true);
                            }}
                            size="small"
                        >
                            Hướng dẫn điền form
                        </Button>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <CollectionForm
                            onImageChange={setImageFile}
                            selectedFileName={imageFile?.name || null}
                            isMobile={isMobile}
                            form={form}
                            imagePreview={imagePreview}
                            imageFile={imageFile}
                            coffees={coffees}
                            drinks={drinks}
                            catalogLoading={catalogLoading}
                            headlineFieldRef={headlineFieldRef}
                            nameFieldRef={nameFieldRef}
                            descriptionFieldRef={descriptionFieldRef}
                            itemsFieldRef={itemsFieldRef}
                            imageFieldRef={imageFieldRef}
                        />
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitLoading}
                                disabled={submitLoading}
                                className="admin-button"
                                ref={submitButtonRef}
                            >
                                {editingCollection ? 'Cập nhật' : 'Thêm mới'}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>

                {/* Modal Tour component */}
                <ConfigProvider locale={viVN}>
                    <Tour
                        open={isModalTourOpen}
                        onClose={() => setIsModalTourOpen(false)}
                        steps={modalTourSteps}
                        indicatorsRender={(current, total) => (
                            <span>{current + 1} / {total}</span>
                        )}
                        type="primary"
                        arrow={true}
                        placement="bottom"
                        mask={{
                            style: {
                                boxShadow: 'inset 0 0 15px #333',
                            },
                        }}
                    />
                </ConfigProvider>
            </Modal>
        </AdminLayout>
    );
};

export default CollectionManagement;